// Settings (Profile + Appearance screens) backed by the real Go backend's
// GET/POST /profile, via the /api-auth proxy prefix (same server authApi.ts
// talks to). POST /profile rewrites the whole stored document (no partial
// merge - see backend/workflows/user/update.wsl), so syncProfile always
// sends the full known profile+theme+locale together regardless of which
// settings screen triggered the save; otherwise one screen's save would
// blank out the other's fields.
//
// Nickname isn't sent here - user/update.wsl still keeps it immutable via
// THIS endpoint - but it's no longer permanently fixed: renameNickname
// below hits the dedicated POST /profile/nickname migration endpoint
// instead (see backend internal/rename.RenameUserNickname). security.key/
// secret aren't exposed by GET /profile at all, so the Security screen
// stays local-only.
import type { AppLanguage, ProfileDraft, ThemeMode } from '../types';
import { readCsrfCookie, reportHttpStatus } from './dataApi';

const API_BASE = import.meta.env.VITE_AUTH_API_BASE ?? '/api-auth';

export interface RemoteSettings {
  profile: Partial<ProfileDraft>;
  themeMode?: ThemeMode;
  language?: AppLanguage;
}

function splitName(fullname: string): { name: string; surname: string } {
  const trimmed = fullname.trim();
  const spaceIndex = trimmed.indexOf(' ');
  if (spaceIndex === -1) return { name: trimmed, surname: '' };
  return { name: trimmed.slice(0, spaceIndex), surname: trimmed.slice(spaceIndex + 1) };
}

export async function fetchProfile(email: string): Promise<RemoteSettings | null> {
  try {
    // credentials: 'include' - GET /profile now requires a session (its
    // response carries PII: email, phone, social handles). The cookie is
    // the only credential the web app has; without this the backend 401s.
    const response = await fetch(`${API_BASE}/profile?email=${encodeURIComponent(email)}`, {
      credentials: 'include',
    });
    reportHttpStatus(response.status);
    if (!response.ok) return null;
    const body = await response.json();
    const data = body?.data;
    if (!body?.success || !data || typeof data !== 'object' || data.error) return null;
    return {
      profile: {
        // Real, immutable userId (a UUID) - was already in this response,
        // just never read. Needed for anything that has to match this
        // account against a channel's ownerIds or a channel:member:*
        // record, both of which key by userId now, not nickname (see
        // StoreContext.tsx's isChannelOwner comment).
        userId: data.id ?? '',
        name: data.name?.fullname ?? '',
        nickname: data.nickname ?? '',
        phone: data.personal?.phone ?? '',
        website: data.social?.website ?? '',
        instagram: data.social?.instagram ?? '',
        whatsapp: data.social?.whatsapp ?? '',
        messenger: data.social?.messenger ?? '',
        telegram: data.social?.telegram ?? '',
        linkedin: data.social?.linkedin ?? '',
        avatarSymbol: data.personal?.avatar?.icon ?? '',
        avatarImageKey: data.personal?.avatar?.image ?? '',
        // Server-owned pricing tier (see AccountTier). GET /profile exposes
        // only the safe subset under `tier`; the fuller billing state (usage,
        // limits, period end) comes from GET /billing/me via billingApi.ts.
        tier: (data.tier?.id as ProfileDraft['tier']) ?? 'free',
        // Per-account flag set from ZMAP (flags.diagnosticsServerPipeline) -
        // gates the Diagnostics screen's internal "Server pipeline" section.
        showServerPipeline: data.flags?.diagnosticsServerPipeline === true,
      },
      themeMode: (data.settings?.theme as ThemeMode) || undefined,
      language: (data.settings?.locale as AppLanguage) || undefined,
    };
  } catch {
    return null;
  }
}

export async function syncProfile(profile: ProfileDraft, themeMode: ThemeMode, language: AppLanguage): Promise<void> {
  const { name, surname } = splitName(profile.name);
  const csrf = readCsrfCookie();
  const response = await fetch(`${API_BASE}/profile`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf } : { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      surname,
      fullname: profile.name,
      phone: profile.phone,
      website: profile.website,
      instagram: profile.instagram,
      whatsapp: profile.whatsapp,
      messenger: profile.messenger,
      telegram: profile.telegram,
      linkedin: profile.linkedin,
      publicEmail: '',
      avatarSymbol: profile.avatarSymbol,
      avatarImageKey: profile.avatarImageKey,
      locale: language,
      theme: themeMode,
    }),
  });

  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // no JSON body
  }
  reportHttpStatus(response.status);
  const data = body?.data;
  if (response.ok && data && typeof data === 'object' && !data.error) return;
  throw new Error((data && typeof data === 'object' && data.error) || `Request failed (${response.status})`);
}

// GET /profile/availability/{nickname} - already existed server-side but
// had no client caller until now. Public (no auth needed) - matches
// backend/workflows/user/availability.wsl's own require list.
export async function checkUserNicknameAvailable(nickname: string): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/profile/availability/${encodeURIComponent(nickname)}`);
    reportHttpStatus(response.status);
    if (!response.ok) return false;
    const body = await response.json();
    const data = body?.data;
    return !!body?.success && data && typeof data === 'object' && data.taken === false;
  } catch {
    return false;
  }
}

// POST /profile/nickname - changes the caller's own nickname (see backend
// internal/rename.RenameUserNickname). currentNickname is only an
// optimistic hint for the server's own log/UI purposes - the backend
// always re-reads the authoritative current value itself.
export async function renameNickname(newNickname: string, currentNickname: string): Promise<string> {
  const csrf = readCsrfCookie();
  const response = await fetch(`${API_BASE}/profile/nickname`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf } : { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nickname: newNickname, currentNickname }),
  });
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // no JSON body
  }
  reportHttpStatus(response.status);
  const data = body?.data;
  if (response.ok && data && typeof data === 'object' && !data.error) return data.nickname ?? newNickname;
  throw new Error((data && typeof data === 'object' && data.error) || `Request failed (${response.status})`);
}

// GDPR self-serve data + deletion (roadmap item #1), both cookie-session
// authenticated like syncProfile above.

export interface ScheduledDeletion {
  scheduled: boolean;
  purgeAfter: string;
  graceDays: number;
}

// DELETE /profile - schedules permanent deletion after a grace window
// (default 7 days). The account is deactivated immediately; signing back
// in before purgeAfter cancels it. Backend cascade (owned channels,
// templates, messages) runs in the janitor once the window elapses.
export async function requestAccountDeletion(): Promise<ScheduledDeletion> {
  const csrf = readCsrfCookie();
  const response = await fetch(`${API_BASE}/profile`, {
    method: 'DELETE',
    credentials: 'include',
    headers: csrf ? { 'X-CSRF-Token': csrf } : {},
  });
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // no JSON body
  }
  reportHttpStatus(response.status);
  const data = body?.data;
  if (response.ok && data && typeof data === 'object' && !data.error && data.scheduled) {
    return { scheduled: true, purgeAfter: data.purgeAfter ?? '', graceDays: data.graceDays ?? 0 };
  }
  throw new Error((data && typeof data === 'object' && data.error) || `Request failed (${response.status})`);
}

// GET /profile/export - downloads the caller's full data bundle (profile,
// channels, authored signals) as a JSON file. Returns the parsed bundle
// too, in case the caller wants to show a summary.
export async function exportAccountData(): Promise<unknown> {
  const response = await fetch(`${API_BASE}/profile/export`, { credentials: 'include' });
  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // no JSON body
  }
  reportHttpStatus(response.status);
  const data = body?.data;
  if (!response.ok || !body?.success || !data || typeof data !== 'object' || data.error) {
    throw new Error((data && typeof data === 'object' && data.error) || `Request failed (${response.status})`);
  }

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `zmist-data-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return data;
}
